import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import type { FilterConfig } from '@openx/types/chipFilters';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FastFiltersMetricRangeProps {
  filterConfig: FilterConfig;
  minValue?: number;
  maxValue?: number;
  onApply: (minValue?: number, maxValue?: number) => void;
  controlHeight?: number;
  dataTest?: string;
}

const toInputValue = (value?: number): string => (value === undefined ? '' : String(value));

const parseOptionalNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const isValidInput = (value: string, min: number, max: number, maxFractionDigits: number): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return true;

  if (!/^-?\d+(?:\.\d+)?$/.test(trimmed)) return false;

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < min || parsed > max) return false;

  const fractionDigits = trimmed.split('.')[1]?.length ?? 0;
  return fractionDigits <= maxFractionDigits;
};

export const FastFiltersMetricRange = ({
  filterConfig,
  minValue,
  maxValue,
  onApply,
  controlHeight = 40,
  dataTest = 'deals-fast-metric-range',
}: FastFiltersMetricRangeProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftMinValue, setDraftMinValue] = useState(() => toInputValue(minValue));
  const [draftMaxValue, setDraftMaxValue] = useState(() => toInputValue(maxValue));
  const numberFilter = filterConfig.numberFilter;
  const open = Boolean(anchorEl);
  const appliedMinValue = toInputValue(minValue);
  const appliedMaxValue = toInputValue(maxValue);
  const isActive = minValue != null || maxValue != null;
  const hasDraftChanges = draftMinValue !== appliedMinValue || draftMaxValue !== appliedMaxValue;
  const parsedMinValue = parseOptionalNumber(draftMinValue);
  const parsedMaxValue = parseOptionalNumber(draftMaxValue);
  const isValid =
    !!numberFilter &&
    isValidInput(draftMinValue, numberFilter.min, numberFilter.max, numberFilter.maxFractionDigits) &&
    isValidInput(draftMaxValue, numberFilter.min, numberFilter.max, numberFilter.maxFractionDigits) &&
    (parsedMinValue === undefined || parsedMaxValue === undefined || parsedMinValue <= parsedMaxValue);
  const isClearDisabled = !draftMinValue && !draftMaxValue && !isActive;

  const synchronizeDraft = useCallback(() => {
    setDraftMinValue(appliedMinValue);
    setDraftMaxValue(appliedMaxValue);
  }, [appliedMaxValue, appliedMinValue]);

  useEffect(() => {
    if (!open) {
      synchronizeDraft();
    }
  }, [open, synchronizeDraft]);

  const handleApply = () => {
    if (!isValid) return;

    onApply(parsedMinValue, parsedMaxValue);
    setAnchorEl(null);
  };

  const handleClear = () => {
    setDraftMinValue('');
    setDraftMaxValue('');
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={event => setAnchorEl(event.currentTarget)}
        endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        data-test={dataTest}
        sx={{
          height: controlHeight,
          minWidth: 170,
          justifyContent: 'space-between',
          textTransform: 'none',
          px: 1.5,
          backgroundColor: theme =>
            isActive
              ? alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)
              : theme.palette.background.paper,
          borderColor: theme => (isActive ? theme.palette.primary.main : theme.palette.divider),
          color: 'text.primary',
          '&:hover': {
            backgroundColor: theme =>
              isActive
                ? alpha(
                    theme.palette.primary.main,
                    theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity
                  )
                : theme.palette.action.hover,
            borderColor: theme => (isActive ? theme.palette.primary.main : theme.palette.divider),
          },
        }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          <Box component="span" sx={{ color: 'text.primary', fontWeight: isActive ? 700 : 500 }}>
            {filterConfig.name}
          </Box>
          {isActive && (
            <Box
              component="span"
              sx={{
                alignItems: 'center',
                backgroundColor: theme => theme.palette.primary.main,
                borderRadius: 1,
                color: theme => theme.palette.primary.contrastText,
                display: 'inline-flex',
                fontSize: 12,
                fontWeight: 700,
                height: 20,
                justifyContent: 'center',
                minWidth: 20,
                px: 0.5,
              }}
            >
              1
            </Box>
          )}
        </Box>
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Paper sx={{ p: 1.5, width: 320 }}>
          <Box data-test={`${dataTest}-section`} sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
            <TextField
              type="number"
              placeholder={t('Min')}
              value={draftMinValue}
              onChange={event => setDraftMinValue(event.target.value)}
              size="small"
              variant="outlined"
              sx={{ flex: 1 }}
              slotProps={{ htmlInput: { 'data-test': `${dataTest}-section-min-input` } }}
            />
            <Box component="span" sx={{ color: 'text.secondary' }}>
              -
            </Box>
            <TextField
              type="number"
              placeholder={t('Max')}
              value={draftMaxValue}
              onChange={event => setDraftMaxValue(event.target.value)}
              size="small"
              variant="outlined"
              sx={{ flex: 1 }}
              slotProps={{ htmlInput: { 'data-test': `${dataTest}-section-max-input` } }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 1 }}>
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={!hasDraftChanges || !isValid}
              data-test="fast-filters-apply-button"
            >
              {t('Apply')}
            </Button>
            <Button
              variant="text"
              onClick={handleClear}
              disabled={isClearDisabled}
              sx={{ color: isClearDisabled ? undefined : 'text.secondary' }}
            >
              {t('Clear Filters')}
            </Button>
          </Box>
        </Paper>
      </Popover>
    </>
  );
};
